import React, { useEffect, useState, useRef } from"react";
import SidebarTechTree from"./SidebarTechTree";
import EliteMarkdownViewer from"./EliteMarkdownViewer";
import ThemeToggle from"./ThemeToggle";
import LockedPopup from"../components/LockedPopup";
import { 
  BookOpen, 
  ChevronDown, 
  Search,
  Clock,
  ThumbsUp,
  ThumbsDown,
  Rocket
} from "lucide-react";
import ChatBot from"../components/SkillGenBot/ChatBot";
import API_BASE_URL from"../Config/config";
import { hasFullAccess } from"../utils/paymentStatus";
import { useTheme } from "../contexts/ThemeContext";
import { useTenant } from "../Context/TenantContext";

function slug(str) {
 return str.replace(/[^a-zA-Z0-9]/g,"").toLowerCase();
}

const TechTreeReader = () => {
  const [technologies, setTechnologies] = useState([]);
  const [selected, setSelected] = useState({ tech: null, topic: null, subtopic: null });
  const [mapping, setMapping] = useState(null);
  const [isPaid, setIsPaid] = useState(false);
  const [visitedPages, setVisitedPages] = useState({});
  const [showPopup, setShowPopup] = useState(false);
  const { theme } = useTheme();
  const { tenant } = useTenant();
  const isDark = theme === "dark";

  const [showChat, setShowChat] = useState(false);
 const chatBotRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const user = JSON.parse(localStorage.getItem("user") || "{}");
        const userId = user?.userId || user?.user_id;
        
        // ✅ Student Tenant Access Bypass
        // Grant full access if:
        // 1. User object explicitly has tenant/college property OR
        // 2. We are currently in a tenant context (resolved from hostname)
        const host = window.location.hostname;
        const isTenantHost = host !== 'theskillgenie.com' && 
                            host !== 'www.theskillgenie.com' && 
                            host !== 'localhost' &&
                            host !== '127.0.0.1';

        if (user && (user.role === 'student' || user.role === 'tenant-student' || user.role === 'admin') && (user.tenant || user.college_id || tenant || isTenantHost)) {
          setIsPaid(true);
          return;
        }

        if (!userId) {
          setIsPaid(false);
          return;
        }

        const userHasFullAccess = await hasFullAccess(userId);
        setIsPaid(userHasFullAccess);
      } catch {
        setIsPaid(false);
      }
    })();
  }, []);

 useEffect(() => {
 import("../data/technology_topics.json").then((mod) => {
 setTechnologies(mod.technologies);
 });
 }, []);

 useEffect(() => {
 if (!selected.tech) return;
 fetch(`/docs/${selected.tech}/mapping.json`)
 .then((res) => res.json())
 .then(setMapping)
 .catch(() => setMapping(null));
 }, [selected.tech]);

 let filePath = null;
 if (mapping && selected.subtopic) {
 const slugged = slug(selected.subtopic);
 filePath = mapping[slugged] ? `/docs/${selected.tech}/${mapping[slugged]}` : null;
 }

 const isLockedView =
 !isPaid &&
 selected.tech &&
 selected.subtopic &&
 Array.isArray(visitedPages[selected.tech]) &&
 !visitedPages[selected.tech].includes(selected.subtopic) &&
 visitedPages[selected.tech].length >= 2;

 const handleSelect = (tech, topic, subtopic) => {
 const alreadyVisited = visitedPages[tech]?.includes(subtopic);
 const visitedCount = visitedPages[tech]?.length || 0;

 if (!isPaid && visitedCount >= 2 && !alreadyVisited) {
 setShowPopup(true);
 return;
 }

 setSelected({ tech, topic, subtopic });

 if (!isPaid && !alreadyVisited) {
 setVisitedPages((prev) => ({
 ...prev,
 [tech]: [...(prev[tech] || []), subtopic],
 }));
 }
 };

  return (
    <div className={`${isDark ? "bg-[#0B0F1A]" : "bg-[#F8FAFC]"} min-h-screen flex flex-col font-sans selection:bg-primary/30`}>
      {/* 🧭 Top Navigation Bar */}
      <header className={`h-16 flex items-center justify-between px-6 sticky top-0 z-[70] border-b backdrop-blur-md ${
        isDark ? "bg-[#0B0F1A]/80 border-white/5" : "bg-primary border-primary"
      }`}>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center shadow-lg ${isDark ? "bg-primary shadow-primary/20" : "bg-white shadow-black/10"}`}>
              <BookOpen className={isDark ? "text-white" : "text-primary"} size={18} />
            </div>
            <span className={`text-lg font-bold tracking-tight ${isDark ? "text-white" : "text-white"}`}>
              The Skill Genie
            </span>
          </div>
          <div className={`h-6 w-px mx-2 ${isDark ? "bg-white/10" : "bg-white/20"}`} />
          <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors ${
            isDark ? "bg-white/5 border-white/10 text-gray-300" : "bg-white/10 border-white/20 text-white"
          }`}>
            <span>Tech Library</span>
            <ChevronDown size={14} className="opacity-50" />
          </div>
        </div>

        <div className="flex-1 max-w-2xl mx-12 hidden md:block">
          <div className={`relative group flex items-center ${isDark ? "text-gray-400" : "text-white/70"}`}>
            <Search className={`absolute left-3 transition-colors ${isDark ? "group-focus-within:text-primary" : "group-focus-within:text-white"}`} size={18} />
            <input 
              type="text" 
              placeholder="Search documentation..." 
              className={`w-full bg-transparent border rounded-xl pl-10 pr-16 py-2 text-sm outline-none transition-all ${
                isDark 
                ? "border-white/10 focus:border-primary/50 focus:bg-white/5" 
                : "border-white/20 focus:border-white/40 focus:bg-white/10 placeholder-white/50 text-white"
              }`}
            />
            <div className={`absolute right-3 px-1.5 py-0.5 rounded border text-[10px] font-mono ${
              isDark ? "border-white/10 text-gray-500" : "border-white/20 text-white/50"
            }`}>
              Ctrl K
            </div>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <ThemeToggle />
          {!isDark && (
             <button className="px-4 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white text-sm font-bold transition-all border border-white/20">
               Login
             </button>
          )}
          <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-xs ${
            isDark ? "bg-primary/20 text-primary" : "bg-secondary text-white shadow-md"
          }`}>
            SP
          </div>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden relative">
        {/* 📚 Sidebar */}
        <aside className={`
          w-80 border-r transition-all duration-300 ease-in-out
          sticky top-0 h-[calc(100vh-64px)] overflow-y-auto
          ${isDark ? "bg-[#0B0F1A] border-white/5" : "bg-white border-gray-100"}
        `}>
          <div className="h-full flex flex-col">
            <div className="p-4 uppercase text-[10px] font-bold tracking-[0.2em] text-gray-500">
              Tech Tree Documents
            </div>
            <div className="flex-1 overflow-y-auto">
              <SidebarTechTree
                technologies={technologies}
                selected={selected}
                onSelect={handleSelect}
                isPaid={isPaid}
                visitedPages={visitedPages}
                theme={theme}
              />
            </div>
          </div>
        </aside>

        {/* 📄 Content area */}
        <main className="flex-1 overflow-y-auto relative bg-transparent scroll-smooth">
          {filePath ? (
            <div className="max-w-6xl mx-auto px-6 py-8 relative z-10">
               {/* Top Meta info */}
               <div className="flex items-center justify-between mb-8 text-[13px]">
                <div className="flex items-center gap-3 text-gray-500">
                  <span className="opacity-60">You're reading</span>
                  <div className="flex items-center gap-2">
                    <span className={`font-semibold ${isDark ? "text-gray-300" : "text-slate-700"}`}>
                      {selected.subtopic}
                    </span>
                    <span className="opacity-30">•</span>
                    <div className="flex items-center gap-1.5">
                      <div className="w-4 h-4 rounded-full border-2 border-primary/30 flex items-center justify-center">
                        <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                      </div>
                      <span>25% complete</span>
                    </div>
                    <span className="opacity-30">•</span>
                    <div className="flex items-center gap-1.5">
                      <Clock className="w-3.5 h-3.5" />
                      <span>5 min read</span>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center gap-6">
                  <div className="flex items-center gap-3">
                    <span className="text-gray-500">Was this helpful?</span>
                    <div className="flex items-center gap-2">
                      <button 
                        onClick={() => alert("Thank you for your feedback!")}
                        className={`p-1.5 rounded-lg border transition-all ${
                        isDark ? "border-white/10 hover:bg-white/5 text-gray-400" : "border-gray-200 hover:bg-gray-50 text-gray-500"
                      }`}>
                        <ThumbsUp className="w-4 h-4" />
                      </button>
                      <button 
                        onClick={() => alert("Thank you for your feedback! We will try to improve.")}
                        className={`p-1.5 rounded-lg border transition-all ${
                        isDark ? "border-white/10 hover:bg-white/5 text-gray-400" : "border-gray-200 hover:bg-gray-50 text-gray-500"
                      }`}>
                        <ThumbsDown className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <EliteMarkdownViewer filePath={filePath} locked={isLockedView} />
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-center max-w-lg mx-auto p-10">
              <div className="w-24 h-24 rounded-3xl bg-primary/10 flex items-center justify-center mb-8 border border-primary/20 shadow-inner">
                <span className="text-5xl animate-bounce-slow">🚀</span>
              </div>
              <h3 className={`text-2xl font-bold mb-4 ${isDark ? "text-white" : "text-slate-900"}`}>Technology EXPLORER</h3>
              <p className="text-slate-500 text-lg leading-relaxed">
                Select a topic from the sidebar to begin your journey to mastery.
              </p>
            </div>
          )}

          <div className="mt-auto text-[10px] font-bold uppercase tracking-widest text-muted text-center w-full pb-4 relative z-10">
            © {new Date().getFullYear()} Evolve X Innovations • TheSkillGenie
          </div>
        </main>

        <LockedPopup show={showPopup} onClose={() => setShowPopup(false)} />
      </div>

      <ChatBot 
        chatBotRef={chatBotRef} 
        setShowChat={setShowChat} 
        showChat={showChat} 
      />
    </div>
  );
};

export default TechTreeReader;



